import { HttpClient, HttpParams } from '@angular/common/http';
import { EventEmitter, inject, Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';

import { SHARED_ENVIRONMENT } from '@k12/environment';
import { BehaviorSubject, finalize, Observable, of } from 'rxjs';
import { GlobalSearchResult, GlobalSearchSectionName } from '@k12/models';
import { LoadingSpinnerService } from '@k12/loading';

import { environment } from '../../../../schools/src/environments/environment';
import { signOutRedirectUri } from '../utils/chrome-free-routes';

@Injectable({
  providedIn: 'root'
})
export class SystemService {
  private readonly _unreadMessageCount = new BehaviorSubject<number>(0);
  private readonly httpClient = inject(HttpClient);
  private readonly msalService = inject(MsalService);
  private readonly reloadHeader = new EventEmitter<number>();
  private readonly sharedEnvironment = inject(SHARED_ENVIRONMENT);
  private readonly spinnerService = inject(LoadingSpinnerService);

  public readonly unreadMessageCount$ = this._unreadMessageCount.asObservable();

  public get unreadMessageCount(): number {
    return this._unreadMessageCount.value;
  }

  public set unreadMessageCount(value: number) {
    this._unreadMessageCount.next(value);
  }

  public getData(): Observable<unknown> {
    return this.httpClient.get<unknown>('https://jsonplaceholder.typicode.com/posts/1');
  }

  public getGlobalSearch(
    value: string,
    adminurl: string,
    section?: GlobalSearchSectionName,
    offset?: number,
    limit?: number
  ): Observable<GlobalSearchResult> {
    this.spinnerService.showSpinner();
    let params = new HttpParams();
    if (section !== undefined && section !== null) {
      params = params.set('section', section);
    }
    if (offset !== undefined && offset !== null) {
      params = params.set('offset', offset.toString());
    }
    if (limit !== undefined && limit !== null) {
      params = params.set('limit', limit.toString());
    }
    return this.httpClient
      .get<GlobalSearchResult>(`${adminurl}/globalSearch/${encodeURIComponent(value)}`, { params })
      .pipe(finalize(() => this.spinnerService.hideSpinner()));
  }

  public isAuthenticated(): Observable<boolean> {
    const token = localStorage.getItem('token');
    return of(!!token);
  }

  public isoDateToFormatted(isoString: string): string {
    const date = new Date(isoString);
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-indexed
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12; // Handle 0 (midnight) as 12
    return `${month}/${day}/${year} ${hours}:${minutes}${ampm}`;
  }

  public login(payload: string): void {
    localStorage.setItem('token', JSON.stringify(payload));
  }

  public logout(): void {
    localStorage.clear();
    sessionStorage.clear();
    document.cookie.split(';').forEach(function (c) {
      document.cookie = c.replace(/^ +/, '').replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
    });

    this.msalService.logoutRedirect({
      account: this.msalService.instance.getActiveAccount(),
      postLogoutRedirectUri: signOutRedirectUri(environment),
      authority: environment.msalAuthority
    });
  }

  public updateUnreadMessageCount(count: number): void {
    this.unreadMessageCount = count;
    this.reloadHeader.emit(count);
  }
}
